import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Typography,
  Modal,
  Space,
  Descriptions,
  Avatar,
  Badge,
  notification,
  Image,
  Tag,
} from "antd";
import {
  DeleteTwoTone,
  InfoCircleOutlined,
  ExclamationCircleOutlined,
  UserOutlined,
} from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import axios from "axios";

const { Text, Paragraph } = Typography;
const { confirm } = Modal;

const Signalement = () => {
  const [data, setData] = useState([]);
  const [refetch, setRefetch] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState({});

  const handleRefetch = () => {
    setRefetch(!refetch);
  };

  const showDeleteConfirm = (reportData) => {
    confirm({
      title: `Voulez-vous supprimer ce signalement ?`,
      icon: <ExclamationCircleOutlined />,
      async onOk() {
        setIsLoading(true);
        try {
          await axios.delete(`https://trend-me.com/Reports/${reportData._id}`);
          handleRefetch();
          notification.success({
            message: "Signalement supprimé avec succès",
          });
        } catch (err) {
          console.error(err);
          notification.error({
            message: "Erreur lors de la suppression",
            description: err.message,
          });
        } finally {
          setIsLoading(false);
        }
      },
      onCancel() {},
    });
  };

  // Fonctions pour l'approbation et le rejet supprimées

  const columns = [
    {
      title: "Établissement",
      dataIndex: ["postId", "etablissement", "name"],
      key: "etablissement",
      render: (text, record) => (
        <Space>
          <Avatar
            size="small"
            src={
              record.postId?.etablissement?.avatar
                ? `https://trend-me.com/upload/${record.postId?.etablissement?.avatar}`
                : null
            }
            icon={!record.postId?.etablissement?.avatar && <UserOutlined />}
          />
          <Text strong>{text || "N/A"}</Text>
        </Space>
      ),
    },
    {
      title: "Utilisateur",
      dataIndex: ["postId", "username"],
      key: "username",
    },
    {
      title: "Type Post",
      dataIndex: ["postId", "type"],
      key: "type",
      render: (type) => <Tag color="blue">{type || "N/A"}</Tag>,
    },
    {
      title: "Raison du signalement",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      render: (text) => <Text type="danger">{text}</Text>,
    },
    {
      title: "Date Post",
      dataIndex: ["postId", "creationDate"],
      key: "creationDate",
      render: (date) => <Text>{date || "N/A"}</Text>,
    },
    {
      title: "Statut Post",
      dataIndex: ["postId", "status"],
      key: "status",
      render: (status) => (
        <Badge
          status={status === "Valid" ? "success" : "error"}
          text={status || "N/A"}
        />
      ),
    },
    {
      title: "Actions",
      key: "action",
      fixed: "right",
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            onClick={() => {
              setIsModalOpen(true);
              setSelectedReport(record);
            }}
          >
            <InfoCircleOutlined /> Détails
          </Button>
          <Button danger onClick={() => showDeleteConfirm(record)}>
            <DeleteTwoTone twoToneColor="red" />
          </Button>
        </Space>
      ),
    },
  ];

  useEffect(() => {
    const fetchReports = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get("https://trend-me.com/Reports");
        if (response.data) {
          setData(response.data);
        } else {
          notification.error({ message: "Aucune donnée trouvée" });
        }
      } catch (error) {
        notification.error({
          message: "Erreur de chargement des données",
          description: error.message,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchReports();
  }, [refetch]);

  return (
    <div className="tabled">
      <Row gutter={[24, 0]}>
        <Col xs="24" xl={24}>
          <Card
            bordered={false}
            loading={isLoading}
            className="criclebox tablespace mb-24"
            title="Gestion des Signalements"
            extra={
              <Button type="primary" onClick={handleRefetch}>
                Rafraîchir
              </Button>
            }
          >
            <Table
              columns={columns}
              dataSource={data}
              pagination={{ pageSize: 10 }}
              rowKey="_id"
              scroll={{ x: 1500 }}
            />
          </Card>
        </Col>
      </Row>

      <Modal
        title="Détails du Signalement"
        visible={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setIsModalOpen(false)}>
            Fermer
          </Button>,
        ]}
      >
        <Descriptions title="Informations du Signalement" bordered column={1}>
          <Descriptions.Item label="Raison du Signalement">
            <Text type="danger" strong>
              {selectedReport?.description}
            </Text>
          </Descriptions.Item>
        </Descriptions>

        <Descriptions
          title="Informations du Post"
          bordered
          column={2}
          style={{ marginTop: 16 }}
        >
          <Descriptions.Item label="Type">
            <Tag color="blue">{selectedReport?.postId?.type}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Utilisateur">
            <Text strong>{selectedReport?.postId?.username}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Date de Création">
            {selectedReport?.postId?.creationDate}
          </Descriptions.Item>
          <Descriptions.Item label="Date de Validation">
            {selectedReport?.postId?.validationDate}
          </Descriptions.Item>
          <Descriptions.Item label="Statut">
            <Badge
              status={
                selectedReport?.postId?.status === "Valid" ? "success" : "error"
              }
              text={selectedReport?.postId?.status}
            />
          </Descriptions.Item>
          <Descriptions.Item label="Likes">
            {selectedReport?.postId?.likes}
          </Descriptions.Item>
          <Descriptions.Item label="Commentaires">
            {selectedReport?.postId?.comments}
          </Descriptions.Item>
          <Descriptions.Item label="URL" span={2}>
            <a
              href={selectedReport?.postId?.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {selectedReport?.postId?.url}
            </a>
          </Descriptions.Item>
          <Descriptions.Item label="Description" span={2}>
            <Paragraph ellipsis={{ rows: 3, expandable: true, symbol: "plus" }}>
              {selectedReport?.postId?.description}
            </Paragraph>
          </Descriptions.Item>
          <Descriptions.Item label="Média" span={2}>
            {selectedReport?.postId?.mediaUrl && (
              <Image
                width={200}
                src={`https://trend-me.com/upload/${selectedReport?.postId?.mediaUrl}`}
                fallback="data:image/png;base64,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data:image/png;base64,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"
              />
            )}
          </Descriptions.Item>
        </Descriptions>

        <Descriptions
          title="Informations de l'Établissement"
          bordered
          column={2}
          style={{ marginTop: 16 }}
        >
          <Descriptions.Item label="Nom">
            {selectedReport?.postId?.etablissement?.name}
          </Descriptions.Item>
          <Descriptions.Item label="Ville">
            {selectedReport?.postId?.etablissement?.ville}{" "}
            {selectedReport?.postId?.etablissement?.codePostal}
          </Descriptions.Item>
          <Descriptions.Item label="Adresse" span={2}>
            {selectedReport?.postId?.etablissement?.location}
          </Descriptions.Item>
          <Descriptions.Item label="Description" span={2}>
            <Paragraph ellipsis={{ rows: 3, expandable: true, symbol: "plus" }}>
              {selectedReport?.postId?.etablissement?.description}
            </Paragraph>
          </Descriptions.Item>
          <Descriptions.Item label="Logo" span={2}>
            {selectedReport?.postId?.etablissement?.avatar && (
              <Avatar
                size={64}
                src={`https://trend-me.com/upload/${selectedReport?.postId?.etablissement?.avatar}`}
                icon={
                  !selectedReport?.postId?.etablissement?.avatar && (
                    <UserOutlined />
                  )
                }
              />
            )}
          </Descriptions.Item>
        </Descriptions>
      </Modal>
    </div>
  );
};

export default Signalement;
